import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Flex,
  Grid,
  Select,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import type { ReportFilters } from '@/lib/services/helpdeskReportService'
import {
  taskReportService,
  type TaskReportByModuleRow,
  type TaskReportByUserRow,
  type TaskReportCompletionRow,
  type TaskReportFilters,
  type TaskReportGroupRow,
  type TaskReportProductivityRow,
  type TaskReportSlaRow,
} from '@/lib/services/taskReportService'

const GROUP_BY_OPTIONS = [
  { value: 'module', label: 'Module (action type)' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'team', label: 'Team' },
  { value: 'parent_ticket', label: 'Parent ticket' },
]

function SummaryCards({
  loading,
  items,
}: {
  loading: boolean
  items: Array<{ label: string; value: string | number }>
}) {
  return (
    <Grid templateColumns={{ base: '1fr 1fr', lg: `repeat(${Math.min(items.length, 4)}, 1fr)` }} gap={4} mb={6}>
      {loading
        ? Array.from({ length: items.length }).map((_, i) => <Skeleton key={i} height="80px" />)
        : items.map((item) => (
            <Card key={item.label}>
              <CardBody>
                <Stat>
                  <StatLabel>{item.label}</StatLabel>
                  <StatNumber>{item.value}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
          ))}
    </Grid>
  )
}

function GroupTable({ rows, loading, extraColumn }: { rows: TaskReportGroupRow[]; loading: boolean; extraColumn?: string }) {
  if (loading) return <Skeleton height="240px" />

  return (
    <Table size="sm">
      <Thead>
        <Tr>
          {extraColumn && <Th>{extraColumn}</Th>}
          <Th>Label</Th>
          <Th>Key</Th>
          <Th isNumeric>Count</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row, index) => (
          <Tr key={`${row.dimension_key}-${row.days_overdue_bucket ?? index}`}>
            {extraColumn && <Td>{row.days_overdue_bucket ?? '—'}</Td>}
            <Td>{row.label}</Td>
            <Td>{row.dimension_key}</Td>
            <Td isNumeric>{row.count}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  )
}

export default function TaskReportsPage() {
  const [filters, setFilters] = useState<TaskReportFilters>({ group_by: 'module' })
  const [groupBy, setGroupBy] = useState('module')
  const [loading, setLoading] = useState(true)

  const [openData, setOpenData] = useState<{ summary: { total_open: number }; groups: TaskReportGroupRow[] } | null>(null)
  const [overdueData, setOverdueData] = useState<{ summary: { total_overdue: number }; groups: TaskReportGroupRow[] } | null>(null)
  const [byUser, setByUser] = useState<TaskReportByUserRow[]>([])
  const [byModule, setByModule] = useState<TaskReportByModuleRow[]>([])
  const [slaRows, setSlaRows] = useState<TaskReportSlaRow[]>([])
  const [completionRows, setCompletionRows] = useState<TaskReportCompletionRow[]>([])
  const [productivityRows, setProductivityRows] = useState<TaskReportProductivityRow[]>([])
  const [productivityNotes, setProductivityNotes] = useState<Record<string, string>>({})

  useEffect(() => {
    const query = { ...filters, group_by: groupBy as TaskReportFilters['group_by'] }
    setLoading(true)

    Promise.all([
      taskReportService.getOpen(query),
      taskReportService.getOverdue(query),
      taskReportService.getByUser(filters),
      taskReportService.getByModule(filters),
      taskReportService.getSlaCompliance({ ...filters, group_by: 'module' }),
      taskReportService.getCompletionTime(query),
      taskReportService.getProductivity({ ...filters, group_by: 'day' }),
    ])
      .then(([open, overdue, users, modules, sla, completion, productivity]) => {
        setOpenData(open)
        setOverdueData(overdue)
        setByUser(users.data ?? [])
        setByModule(modules.data ?? [])
        setSlaRows(sla.data ?? [])
        setCompletionRows(completion.data ?? [])
        setProductivityRows(productivity.data ?? [])
        const notes = productivity.summary?.notes
        setProductivityNotes(typeof notes === 'object' && notes !== null ? (notes as Record<string, string>) : {})
      })
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id, filters.team_id, groupBy])

  const headerFilters: ReportFilters = {
    client_id: filters.client_id,
    from: filters.from,
    to: filters.to,
    team_id: filters.team_id,
    staging: filters.staging,
  }

  return (
    <Box p={4}>
      <ReportPageHeader
        title="Task Reports"
        subtitle="Open workload, SLA, completion time, and task completion analytics"
        reportType="tasks-open"
        filters={headerFilters}
        onFiltersChange={(f) => setFilters((prev) => ({ ...prev, ...f }))}
        exportDisabled
      />

      <Flex mb={4} align="center" gap={3} flexWrap="wrap">
        <Text fontSize="sm" color="gray.600">
          Group open / overdue / completion by
        </Text>
        <Select size="sm" maxW="240px" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          {GROUP_BY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </Flex>

      <Tabs colorScheme="blue" variant="line">
        <TabList mb={4} flexWrap="wrap">
          <Tab fontWeight="600">Open</Tab>
          <Tab fontWeight="600">Overdue</Tab>
          <Tab fontWeight="600">By user</Tab>
          <Tab fontWeight="600">By module</Tab>
          <Tab fontWeight="600">SLA compliance</Tab>
          <Tab fontWeight="600">Completion time</Tab>
          <Tab fontWeight="600">Task completion</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <SummaryCards
              loading={loading}
              items={[{ label: 'Total open tasks', value: openData?.summary.total_open ?? 0 }]}
            />
            <Card>
              <CardBody overflowX="auto">
                <GroupTable rows={openData?.groups ?? []} loading={loading} />
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <SummaryCards
              loading={loading}
              items={[{ label: 'Total overdue tasks', value: overdueData?.summary.total_overdue ?? 0 }]}
            />
            <Card>
              <CardBody overflowX="auto">
                <GroupTable rows={overdueData?.groups ?? []} loading={loading} extraColumn="Days overdue" />
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Card>
              <CardBody overflowX="auto">
                {loading ? (
                  <Skeleton height="240px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>User</Th>
                        <Th isNumeric>Open</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Overdue</Th>
                        <Th isNumeric>Avg completion (hrs)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {byUser.map((row) => (
                        <Tr key={row.user_id}>
                          <Td>{row.name}</Td>
                          <Td isNumeric>{row.open_count}</Td>
                          <Td isNumeric>{row.completed_count}</Td>
                          <Td isNumeric>{row.overdue_count}</Td>
                          <Td isNumeric>{row.avg_completion_hours ?? '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Card>
              <CardBody overflowX="auto">
                {loading ? (
                  <Skeleton height="240px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Action type</Th>
                        <Th isNumeric>Total</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Completion %</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {byModule.map((row) => (
                        <Tr key={row.action_type}>
                          <Td>{row.action_type}</Td>
                          <Td isNumeric>{row.total_count}</Td>
                          <Td isNumeric>{row.completed_count}</Td>
                          <Td isNumeric>{row.completion_rate_percent}%</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Card>
              <CardBody overflowX="auto">
                {loading ? (
                  <Skeleton height="240px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Module</Th>
                        <Th isNumeric>With due date</Th>
                        <Th isNumeric>On time</Th>
                        <Th isNumeric>Late</Th>
                        <Th isNumeric>Open overdue</Th>
                        <Th isNumeric>Compliance %</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {slaRows.map((row) => (
                        <Tr key={row.action_type ?? 'all'}>
                          <Td>{row.action_type ?? 'All'}</Td>
                          <Td isNumeric>{row.total_with_due}</Td>
                          <Td isNumeric>{row.on_time_count}</Td>
                          <Td isNumeric>{row.late_count}</Td>
                          <Td isNumeric>{row.open_overdue_count}</Td>
                          <Td isNumeric>{row.compliance_percent}%</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            <Card>
              <CardBody overflowX="auto">
                {loading ? (
                  <Skeleton height="240px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Group</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Avg created→done (hrs)</Th>
                        <Th isNumeric>Median created→done (hrs)</Th>
                        <Th isNumeric>Avg accepted→done (hrs)</Th>
                        <Th isNumeric>Median accepted→done (hrs)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {completionRows.map((row) => (
                        <Tr key={row.dimension_key}>
                          <Td>{row.label}</Td>
                          <Td isNumeric>{row.completed_count}</Td>
                          <Td isNumeric>{row.avg_created_to_completed_hours}</Td>
                          <Td isNumeric>{row.median_created_to_completed_hours ?? '—'}</Td>
                          <Td isNumeric>{row.avg_accepted_to_completed_hours ?? '—'}</Td>
                          <Td isNumeric>{row.median_accepted_to_completed_hours ?? '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          <TabPanel px={0}>
            {(productivityNotes.override_complete || productivityNotes.reopen) && (
              <Box mb={3}>
                {productivityNotes.override_complete && (
                  <Text fontSize="xs" color="gray.500">{productivityNotes.override_complete}</Text>
                )}
                {productivityNotes.reopen && (
                  <Text fontSize="xs" color="gray.500">{productivityNotes.reopen}</Text>
                )}
              </Box>
            )}
            <Card>
              <CardBody overflowX="auto">
                {loading ? (
                  <Skeleton height="240px" />
                ) : (
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>User</Th>
                        <Th>Period</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Override complete</Th>
                        <Th isNumeric>Override %</Th>
                        <Th isNumeric>Reopens</Th>
                        <Th isNumeric>Reopen %</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {productivityRows.map((row) => (
                        <Tr key={`${row.user_id}-${row.period}`}>
                          <Td>{row.name}</Td>
                          <Td>{row.period}</Td>
                          <Td isNumeric>{row.completed_count}</Td>
                          <Td isNumeric>{row.override_complete_count}</Td>
                          <Td isNumeric>{row.override_complete_rate_percent}%</Td>
                          <Td isNumeric>{row.reopen_count}</Td>
                          <Td isNumeric>{row.reopen_rate_percent}%</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}
